import React, {FC, useCallback, useState} from 'react';
import styled from 'styled-components';

import {buildInteractionIdProps} from '../../../../../../libs/shared/core/src/helpers/interaction/interactionHelpers';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {CustomError} from '../../../../../../libs/shared/core/src/helpers/types/customErrors';
import {MessageTypesEnum} from '../../../../../../libs/shared/core/src/models/api/commonMessageModel';
import {
  LoadedMessageModel,
  MessageStatusesEnum,
} from '../../../../../../libs/shared/core/src/models/api/messageModel';
import {DataEventActionsEnum} from '../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {LinkedConversationsDataEventObject} from '../../../../../../libs/shared/core/src/models/tracking/objectNames/linkedConversationsDataEventObject';
import {reportToBugsnag} from '../../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {useDataEventTracking} from '../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {ReplyLocationsEnum} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {NewPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Banner, BannerTypesEnum} from '../../../../../../libs/shared/ui/src/components/banners/banner';
import {BannerCloseAction} from '../../../../../../libs/shared/ui/src/components/banners/bannerCloseAction';
import {BannerContent} from '../../../../../../libs/shared/ui/src/components/banners/bannerContent';
import {BannerContentTitle} from '../../../../../../libs/shared/ui/src/components/banners/bannerContentTitle';
import {BannerIcon} from '../../../../../../libs/shared/ui/src/components/banners/bannerIcon';
import {BannerPrioritiesEnum} from '../../../../../../libs/shared/ui/src/components/banners/bannersContext';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {IconButton} from '../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {MultiButtonsDialog} from '../../../../../../libs/shared/ui/src/components/dialogs/multiButtonsDialog';
import {OkCancelDialog} from '../../../../../../libs/shared/ui/src/components/dialogs/okCancelDialog';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {Paragraph} from '../../../../../../libs/shared/ui/src/components/utility/paragraph';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {preventDragStart} from '../../../../../helpers/dragAndDropHelpers';
import {
  buildLegacyEntryPointInstance,
  LegacyMessageMoreDropdownEntryPointInstance,
} from '../../../../../helpers/models/legacyApplicationHelpers';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {LegacyMessageMoreDropdownEntryPointModel} from '../../../../../models/api/legacyEntryPointModel';
import {MacroModel} from '../../../../../models/api/macroModel';
import {
  getDismissedConversationAccessDialog,
  getDismissedDiscussionAccessDialog,
} from '../../../../../store/data/preferences/preferencesSelectors';
import {EmlSource} from '../../../../../store/panels/eml/emlPanelState';
import {LegacyMessageApplicationContextInstanceHost} from '../../../../applications/legacyContextHosts/legacyMessageApplicationContextInstanceHost';
import {CreateCustomerPortalDialog} from '../../../dialogs/createCustomerPortalDialog';
import {SplitErrorDialog} from '../../conversation/actions/split/SplitErrorDialog';
import {useRunMacro} from '../../macroTriggers/useRunMacro';
import {LinkedConversationAccessDialog} from '../linkedConversation/linkedConversationAccessDialog';
import {CloneHandler, ForwardHandler} from './messageViewerMoreDropdown';
import {MessageViewerMoreDropdownContainer} from './messageViewerMoreDropdownContainer';

/*
 * Props.
 */

export interface MessageViewerMoreBaseProps {
  panelId: NewPanelId;
  namespace: string;
  activityId: number | undefined;
  message: LoadedMessageModel;
  isOnlyMessage?: boolean;
  isOnlyActivity?: boolean;
  isFirstMessage?: boolean;
  onReplyClick: (arg: ReplyLocationsEnum) => void;
  onReplyAllClick: (arg: ReplyLocationsEnum) => void;
  onForwardClick: ForwardHandler;
  onCloneClick: CloneHandler;
  onDestroyConversationClick: () => void;
  cellsContext: CellsContext;
  onPopoutClick: () => void;
  emlSource?: EmlSource;
  onPopoverToggle?: (arg: boolean) => void;
}

export interface MessageViewerMoreProps extends MessageViewerMoreBaseProps {
  isUserVisitor: boolean;
  isInTeammateContext: boolean;
  conversation: ConversationModel | undefined;
  onSaveEmail: () => Promise<void>;
  onSplitClick: () => void;
  onDestroyMessageClick: () => void;
  isInDragRegion?: boolean;
  onPopoutLinkedConversationClick: (arg: (arg: number) => void) => void;
  onPopoutLinkedDiscussionClick: (arg: (arg: number) => void) => void;
  hasSmallIcon?: boolean;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  grid-area: more;
`;

/*
 * Component.
 */

export const MessageViewerMore: FC<MessageViewerMoreProps> = (props) => {
  const {panelId, isUserVisitor, message, onSaveEmail, isInDragRegion} = props;

  const [saveEmailError, setSaveEmailError] = useState('');
  const [isSplitConfirmationOpen, setIsSplitConfirmationOpen] = useState(false);
  const [isDestroyConfirmationOpen, setIsDestroyConfirmationOpen] = useState(false);
  const [isCustomerPortalCreateDialogOpen, setIsCustomerPortalCreateDialogOpen] = useState(false);
  const [splitError, setSplitError] = useState<CustomError | undefined>(undefined);

  const [selectedEntryPoint, setSelectedEntryPoint] = useState<LegacyMessageMoreDropdownEntryPointInstance>();
  const [isConversationAccessDialogOpen, setIsConversationAccessDialogOpen] = useState(false);
  const [isDiscussionAccessDialogOpen, setIsDiscussionAccessDialogOpen] = useState(false);
  const dismissedConversationAccessDialog = useAppSelector(getDismissedConversationAccessDialog);
  const dismissedDiscussionAccessDialog = useAppSelector(getDismissedDiscussionAccessDialog);
  const {trackDataEvent} = useDataEventTracking();
  const {onRunMacro, dialog: macroDialog} = useRunMacro(props.conversation?.id);

  const trackCreateLinkedConversationsSuccess = useCallback(
    (targetConversationId: number) => {
      trackDataEvent(
        DataEventActionsEnum.SUCCESS,
        LinkedConversationsDataEventObject.CREATE_LINKED_CONVERSATION,
        {
          sourceConversationId: message.conversationId,
          targetConversationId,
        },
      );
    },
    [message.conversationId, trackDataEvent],
  );

  /*
   * Event handlers.
   */

  // Don't show this menu for draft messages.
  if (isUserVisitor || message.status === MessageStatusesEnum.DRAFT_COMPOSE) {
    return null;
  }

  const onSaveEmailClick = async () => {
    try {
      await onSaveEmail();
    } catch (err) {
      reportToBugsnag(err);
      setSaveEmailError(`We couldn't save the email.`);
    }
  };

  const onEntryPointSelect = (entryPoint: LegacyMessageMoreDropdownEntryPointModel) => {
    setSelectedEntryPoint(buildLegacyEntryPointInstance(entryPoint));
  };

  const onPopoutLinkedConversationClick = () => {
    trackDataEvent(
      DataEventActionsEnum.SELECT,
      LinkedConversationsDataEventObject.CREATE_LINKED_CONVERSATION,
      {
        sourceConversationId: props.conversation?.id,
      },
    );

    if (dismissedConversationAccessDialog) {
      props.onPopoutLinkedConversationClick(trackCreateLinkedConversationsSuccess);
    } else {
      setIsConversationAccessDialogOpen(true);
    }
  };

  const onPopoutLinkedDiscussionClick = () => {
    trackDataEvent(DataEventActionsEnum.SELECT, LinkedConversationsDataEventObject.CREATE_LINKED_DISCUSSION, {
      sourceConversationId: props.conversation?.id,
    });

    if (dismissedDiscussionAccessDialog) {
      props.onPopoutLinkedDiscussionClick(trackCreateLinkedConversationsSuccess);
    } else {
      setIsDiscussionAccessDialogOpen(true);
    }
  };

  const onCreateCustomerPortalConversation = () => {
    // TODO: Add data event tracking.
    setIsCustomerPortalCreateDialogOpen(true);
  };

  const dropdownProps: MessageViewerMoreProps = {
    ...props,
    onReplyAllClick: (location) => props.onReplyAllClick(location),
    onReplyClick: (location) => props.onReplyClick(location),
  };

  const onSplitClick = async () => {
    try {
      await props.onSplitClick();
    } catch (error) {
      if (error instanceof CustomError) {
        setSplitError(error);
        setIsSplitConfirmationOpen(false);
      }
    }
  };

  /*
   * Render.
   */

  return (
    <>
      <StyledWrapperDiv draggable={isInDragRegion} onDragStart={preventDragStart}>
        <PopoverCoordinator
          renderButton={(isOpen) => (
            <MoreButton
              hasSmallIcon={props.hasSmallIcon}
              isOpen={isOpen}
              isOnlyActivity={Boolean(props.isOnlyActivity)}
            />
          )}
          renderModal={(popoverProps) =>
            renderMoreDropdown(
              popoverProps,
              dropdownProps,
              setIsSplitConfirmationOpen,
              setIsDestroyConfirmationOpen,
              onSaveEmailClick,
              onEntryPointSelect,
              onPopoutLinkedConversationClick,
              onPopoutLinkedDiscussionClick,
              onRunMacro,
              onCreateCustomerPortalConversation,
            )
          }
          onPopoverToggle={props.onPopoverToggle}
        />
      </StyledWrapperDiv>
      {macroDialog}
      {saveEmailError && (
        <SaveEmailErrorBanner error={saveEmailError} onDismiss={() => setSaveEmailError('')} />
      )}
      {isSplitConfirmationOpen && (
        <SplitConfirmation
          {...props}
          onRequestClose={() => setIsSplitConfirmationOpen(false)}
          onSplitClick={onSplitClick}
        />
      )}
      {isDestroyConfirmationOpen && (
        <DestroyConfirmation {...props} onRequestClose={() => setIsDestroyConfirmationOpen(false)} />
      )}
      {splitError && (
        <SplitErrorDialog onRequestClose={() => setSplitError(undefined)} splitError={splitError} />
      )}
      {selectedEntryPoint && (
        <LegacyMessageApplicationContextInstanceHost
          panelId={panelId}
          entryPointInstance={selectedEntryPoint}
          message={props.message}
        />
      )}
      {isConversationAccessDialogOpen && (
        <LinkedConversationAccessDialog
          onOkClick={() => {
            props.onPopoutLinkedConversationClick(trackCreateLinkedConversationsSuccess);
            setIsConversationAccessDialogOpen(false);
          }}
          onRequestClose={() => setIsConversationAccessDialogOpen(false)}
        />
      )}
      {isDiscussionAccessDialogOpen && (
        <LinkedConversationAccessDialog
          onOkClick={() => {
            props.onPopoutLinkedDiscussionClick(trackCreateLinkedConversationsSuccess);
            setIsDiscussionAccessDialogOpen(false);
          }}
          onRequestClose={() => setIsDiscussionAccessDialogOpen(false)}
        />
      )}
      {isCustomerPortalCreateDialogOpen && (
        <CreateCustomerPortalDialog
          message={message}
          cellsContext={props.cellsContext}
          onRequestClose={() => setIsCustomerPortalCreateDialogOpen(false)}
        />
      )}
    </>
  );
};

/*
 * Button.
 */

interface MoreButtonProps {
  isOpen: boolean;
  isOnlyActivity: boolean;
  hasSmallIcon?: boolean;
}

const MoreButton = (props: MoreButtonProps) => {
  const theme = useUserTheme();
  const interactionProps = props.isOnlyActivity ? buildInteractionIdProps('message-view-more-button') : {};

  return (
    <div {...interactionProps}>
      <IconButton
        color={theme.greys.shade80}
        hoverColor={theme.greys.shade80}
        iconName={props.hasSmallIcon ? 'moreVerticalSmall' : 'moreVertical'}
        label={<Trans id="message-viewer-more">More</Trans>}
        hasHover={props.isOpen}
      />
    </div>
  );
};

/*
 * Rendered popover.
 */

function renderMoreDropdown(
  popoverProps: PopoverTransferProps,
  props: MessageViewerMoreProps,
  setIsSplitConfirmationOpen: (val: boolean) => void,
  setIsDestroyConfirmationOpen: (val: boolean) => void,
  onSaveEmailClick: () => void,
  onEntryPointSelect: (model: LegacyMessageMoreDropdownEntryPointModel) => void,
  onPopoutLinkedConversationClick: () => void,
  onPopoutLinkedDiscussionClick: () => void,
  onRunMacro: (macro: MacroModel) => void,
  onCreateCustomerPortalConversationClick: () => void,
) {
  const {namespace, cellsContext, conversation} = props;
  const namespaces = [namespace];

  return (
    <MessageViewerMoreDropdownContainer
      {...popoverProps}
      {...props}
      conversation={conversation}
      namespaces={namespaces}
      selectedInboxId={cellsContext.inboxId}
      onSaveEmailClick={onSaveEmailClick}
      onSplitClick={() => setIsSplitConfirmationOpen(true)}
      onDestroyClick={() => setIsDestroyConfirmationOpen(true)}
      onEntryPointSelect={onEntryPointSelect}
      onPopoutLinkedConversationClick={onPopoutLinkedConversationClick}
      onPopoutLinkedDiscussionClick={onPopoutLinkedDiscussionClick}
      onRunMacro={onRunMacro}
      onCreateCustomerPortalConversationClick={onCreateCustomerPortalConversationClick}
    />
  );
}

/*
 * Save email error.
 */

interface SaveEmailErrorBannerProps {
  error: string | undefined;
  onDismiss: () => void;
}

const SaveEmailErrorBanner: FC<SaveEmailErrorBannerProps> = (props) => {
  const theme = useUserTheme();
  const {error, onDismiss} = props;
  const displayedError = error || <Trans id="saveemail-error-content">An unexpected error occurred.</Trans>;

  return (
    <Banner
      bannerType={BannerTypesEnum.MULTILINE}
      color={theme.palette.red.shade40}
      priority={BannerPrioritiesEnum.ERROR}
    >
      <BannerCloseAction onClick={onDismiss} />
      <BannerIcon iconName="warningFilledSmall" />
      <BannerContent>
        <BannerContentTitle>
          <Trans id="saveemail-error-title">Error saving the email</Trans>
        </BannerContentTitle>
        {displayedError}
      </BannerContent>
    </Banner>
  );
};

/*
 * Destroy confirmation.
 */

interface DestroyConfirmationExtraProps {
  onRequestClose: () => void;
}

const DestroyConfirmation: FC<MessageViewerMoreProps & DestroyConfirmationExtraProps> = (props) => {
  const {isOnlyMessage, isOnlyActivity} = props;

  // If this is the only message, but there are messages left, make the user choose.
  if (isOnlyMessage && !isOnlyActivity) {
    return (
      <MultiButtonsDialog
        title={<Trans id="destroy-title">Delete permanently</Trans>}
        primaryButtonLabel={<Trans id="destroy-conversation-button">Delete conversation</Trans>}
        onPrimaryClick={props.onDestroyConversationClick}
        secondaryButtonLabel={<Trans id="destroy-message-button">Delete message</Trans>}
        onSecondaryClick={props.onDestroyMessageClick}
        onRequestClose={props.onRequestClose}
        disableSameWidth
      >
        <Trans id="destroy-either-content">
          <Paragraph>You are about to delete the last message of this conversation.</Paragraph>
          <Paragraph>
            Would you like to delete the individual message, or delete the entire conversation instead?
          </Paragraph>
          <Paragraph>
            This <strong>cannot</strong> be undone.
          </Paragraph>
        </Trans>
      </MultiButtonsDialog>
    );
  }

  // Otherwise, if we're the only message, offer to delete the conversation.
  if (isOnlyMessage) {
    return (
      <OkCancelDialog
        title={<Trans id="destroy-title">Delete permanently</Trans>}
        okButtonLabel={<Trans id="destroy-button">Delete</Trans>}
        onOkClick={props.onDestroyConversationClick}
        okButtonVariant={ButtonVariantsEnum.DANGER}
        onRequestClose={props.onRequestClose}
      >
        <Trans id="destroy-conversation-content">
          This conversation will be permanently deleted from Front.
          <br />
          This <strong>cannot</strong> be undone.
        </Trans>
      </OkCancelDialog>
    );
  }

  // Otherwise, we only offer to delete the message.
  return (
    <OkCancelDialog
      title={<Trans id="destroy-title">Delete permanently</Trans>}
      okButtonLabel={<Trans id="destroy-button">Delete</Trans>}
      onOkClick={props.onDestroyMessageClick}
      okButtonVariant={ButtonVariantsEnum.DANGER}
      onRequestClose={props.onRequestClose}
    >
      <Trans id="destroy-message-content">
        This message will be permanently deleted from Front.
        <br />
        This <strong>cannot</strong> be undone.
      </Trans>
    </OkCancelDialog>
  );
};

/*
 * Move email to new conversation.
 */

interface SplitConfirmationExtraProps {
  onRequestClose: () => void;
}

const SplitConfirmation: FC<MessageViewerMoreProps & SplitConfirmationExtraProps> = (props) => {
  const {message, onSplitClick, onRequestClose} = props;
  if (message.type === MessageTypesEnum.EMAIL) {
    return (
      <OkCancelDialog
        title={<Trans id="splitconversation-email-title">Move to new conversation</Trans>}
        okButtonLabel={<Trans id="splitconversation-email-ok">Move</Trans>}
        onOkClick={onSplitClick}
        onRequestClose={onRequestClose}
      >
        <Trans id="splitconversation-email-content">
          This email will be moved from this conversation to start a new conversation.
        </Trans>
      </OkCancelDialog>
    );
  }

  return (
    <OkCancelDialog
      title={<Trans id="splitconversation-other-title">Split conversation</Trans>}
      okButtonLabel={<Trans id="split-conversation-other-ok">Split</Trans>}
      onOkClick={onSplitClick}
      onRequestClose={onRequestClose}
    >
      <Trans id="splitconversation-other-content">
        This message will be used to start a new conversation.
        <br />
        After the split, you will only be able to reply from the new conversation.
      </Trans>
    </OkCancelDialog>
  );
};
